import type { WSMessage } from '@/types/api';
import { env } from '@/lib/env';

export type WSStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'offline';

type StatusHandler = (status: WSStatus) => void;
type MessageHandler = (msg: WSMessage) => void;

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

class WSClient {
  private ws: WebSocket | null = null;
  private status: WSStatus = 'idle';
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private explicitlyClosed = false;
  private statusHandlers = new Set<StatusHandler>();
  private messageHandlers = new Set<MessageHandler>();
  private reconnectHandlers = new Set<() => void>();

  connect() {
    this.explicitlyClosed = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    try {
      this.ws = new WebSocket(env.VITE_VENDOR_WS_URL);
    } catch (err) {
      console.error('[ws] failed to construct WebSocket', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      const wasReconnect = this.attempt > 0;
      this.attempt = 0;
      this.setStatus('live');
      if (wasReconnect) this.reconnectHandlers.forEach((h) => h());
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as WSMessage;
        this.messageHandlers.forEach((h) => h(msg));
      } catch (err) {
        console.warn('[ws] could not parse message', err, event.data);
      }
    };

    this.ws.onerror = () => {
      // onclose will fire next
    };

    this.ws.onclose = () => {
      this.ws = null;
      if (this.explicitlyClosed) {
        this.setStatus('idle');
        return;
      }
      this.scheduleReconnect();
    };
  }

  disconnect() {
    this.explicitlyClosed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.setStatus('idle');
    this.attempt = 0;
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => {
      this.reconnectHandlers.delete(handler);
    };
  }

  getStatus() {
    return this.status;
  }

  private setStatus(next: WSStatus) {
    if (this.status === next) return;
    this.status = next;
    this.statusHandlers.forEach((h) => h(next));
  }

  private scheduleReconnect() {
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
    this.attempt += 1;
    this.setStatus(this.attempt > 3 ? 'offline' : 'reconnecting');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

export const wsClient = new WSClient();
